import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { X, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const AddCandidatureHSModal = ({ isOpen, onClose, onSuccess, jobOfferId }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    commentaire: ''
  });
  const [files, setFiles] = useState({
    cv: null,
    lettre: null
  });

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (type, file) => {
    if (!file) {
      setFiles(prev => ({
        ...prev,
        [type]: null
      }));
      return;
    }

    // Validation du format de fichier
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format de fichier non autorisé. Seuls les fichiers PDF, DOC et DOCX sont acceptés.');
      return;
    }

    // Validation de la taille (10 Mo max)
    const maxSize = 10 * 1024 * 1024; // 10 MB
    if (file.size > maxSize) {
      toast.error('Le fichier est trop volumineux. Taille maximum autorisée : 10 Mo.');
      return;
    }

    setFiles(prev => ({
      ...prev,
      [type]: file
    }));
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('token');
    const response = await fetch(`${API}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (response.ok) {
      const result = await response.json();
      return result.filename;
    } else {
      throw new Error('Erreur lors de l\'upload du fichier');
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.prenom.trim()) {
      toast.error('Le prénom est requis');
      return;
    }
    if (!formData.nom.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Upload files if present
      let cvFilename = null;
      let lettreFilename = null;

      if (files.cv) {
        cvFilename = await uploadFile(files.cv);
      }

      if (files.lettre) {
        lettreFilename = await uploadFile(files.lettre);
      }

      // Create candidature
      const candidatureData = {
        prenom: formData.prenom.trim(),
        nom: formData.nom.trim(),
        commentaire: formData.commentaire.trim() || null,
        job_offer_id: jobOfferId,
        cv_filename: cvFilename,
        lettre_filename: lettreFilename,
        ajouter_au_vivier: false // Always false for HS
      };

      const response = await fetch(`${API}/candidatures`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(candidatureData),
      });

      if (response.ok) {
        toast.success('Candidature ajoutée avec succès');
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'ajout de la candidature');
      }
    } catch (error) {
      console.error('Error adding candidature:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="candidature-modal-overlay">
      <div className="candidature-modal">
        <Card className="candidature-modal-card">
          <CardHeader className="candidature-modal-header">
            <div className="flex justify-between items-center">
              <CardTitle className="candidature-modal-title">
                Ajouter une candidature - Horizon Solution
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="candidature-modal-close"
              >
                <X size={20} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="candidature-modal-content">
            <form className="candidature-form" onSubmit={(e) => e.preventDefault()}>
              {/* Prénom */}
              <div className="form-group">
                <Label htmlFor="prenom" className="form-label">
                  Prénom *
                </Label>
                <Input
                  id="prenom"
                  value={formData.prenom}
                  onChange={(e) => handleInputChange('prenom', e.target.value)}
                  className="form-input"
                  placeholder="Prénom du candidat"
                />
              </div>

              {/* Nom */}
              <div className="form-group">
                <Label htmlFor="nom" className="form-label">
                  Nom *
                </Label>
                <Input
                  id="nom"
                  value={formData.nom}
                  onChange={(e) => handleInputChange('nom', e.target.value)}
                  className="form-input"
                  placeholder="Nom du candidat"
                />
              </div>

              {/* CV Upload */}
              <div className="form-group">
                <Label htmlFor="cv" className="form-label">
                  CV
                </Label>
                <Input
                  id="cv"
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => handleFileChange('cv', e.target.files[0])}
                  className="form-input file-input"
                />
                {files.cv && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ {files.cv.name}
                  </p>
                )}
              </div>

              {/* Lettre de motivation */}
              <div className="form-group">
                <Label htmlFor="lettre" className="form-label">
                  Lettre de motivation
                </Label>
                <Input
                  id="lettre"
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => handleFileChange('lettre', e.target.files[0])}
                  className="form-input file-input"
                />
                {files.lettre && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ {files.lettre.name}
                  </p>
                )}
              </div>

              {/* Commentaire */}
              <div className="form-group">
                <Label htmlFor="commentaire" className="form-label">
                  Commentaire
                </Label>
                <Textarea
                  id="commentaire"
                  value={formData.commentaire}
                  onChange={(e) => handleInputChange('commentaire', e.target.value)}
                  className="form-textarea"
                  placeholder="Commentaires sur le candidat..."
                  rows={3}
                />
              </div>

              {/* Action buttons */}
              <div className="candidature-actions">
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="btn-primary"
                  style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
                >
                  <Send size={16} className="mr-2" />
                  {loading ? 'Envoi...' : 'Envoyer'}
                </Button>
                <Button
                  onClick={onClose}
                  disabled={loading}
                  variant="outline"
                  className="btn-cancel"
                >
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AddCandidatureHSModal;